import { useToastNotification } from "@coworker/components/src/components/ToastNotification";
import { useTranslation } from "@coworker/locales";
import { useState } from "react";
import { useCloseTask } from "./api/useCloseTask";
import { useMoveUsersToTeam } from "./api/useMoveUsersToTeam";
import { useReassignTasks } from "./api/useReassignTasks";
import {
  isCloseTaskOperation,
  isMoveUsersToTeamOperation,
  isReassignTasksOperation,
  TeamDeletionFlowOperations,
} from "./wizard/TeamDeletionFlowOperations";
import {
  OnWizardStep,
  TeamDeletionWizardSteps,
  useTeamDeletionWizard,
} from "./wizard/useTeamDeletionWizard";

type UseTeamDeletionFlowArgs = {
  wizardSteps: TeamDeletionWizardSteps[];
  onDeleteTeam: () => Promise<void>;
  onFlowComplete: () => void;
};

export const useTeamDeletionFlow = ({
  wizardSteps,
  onDeleteTeam,
  onFlowComplete,
}: UseTeamDeletionFlowArgs) => {
  const { t } = useTranslation();
  const { showToast } = useToastNotification();

  // we keep the operations in a stable, imperative registry
  // so that the individual steps can register/unregister/clear operations
  // without causing unnecessary re-renders
  const [teamDeletionFlowOperations] = useState(
    () => new TeamDeletionFlowOperations()
  );

  const { resignTasks } = useReassignTasks();
  const { closeTask } = useCloseTask();
  const { moveUsersToTeam } = useMoveUsersToTeam();
  const processRegisteredOperations = async () => {
    const operations = teamDeletionFlowOperations.getOperations();
    const promises = operations.map((operation) => {
      if (isReassignTasksOperation(operation)) {
        return resignTasks(operation.payload);
      }
      if (isCloseTaskOperation(operation)) {
        return closeTask(operation.payload);
      }
      if (isMoveUsersToTeamOperation(operation)) {
        return moveUsersToTeam(operation.payload);
      }
      return Promise.resolve();
    });

    await Promise.all(promises);

    teamDeletionFlowOperations.clear();
  };

  // when user goes to completion step, we execute all the operations and then delete the team
  const handleWizardCompletion: OnWizardStep = async ({ step, wizardAPI }) => {
    if (step === TeamDeletionWizardSteps.COMPLETION) {
      try {
        await processRegisteredOperations();
        await onDeleteTeam();
        onFlowComplete();
      } catch {
        showToast(t("errorBoundaryString"));
        wizardAPI.goToPreviousStep();
      }
    }
  };

  const wizard = useTeamDeletionWizard({
    steps: wizardSteps,
    onWizardStep: handleWizardCompletion,
  });

  return { operations: teamDeletionFlowOperations, wizard };
};
